import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import ArtworkModal from './ArtworkModal'; // Adjust the path as needed

const GalleryContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
`;

const ArtworkItem = styled.div`
  margin: 2rem;
  width: ${props => `calc(${props.width}% - 20px)`};
  flex-grow: 1;
  text-align: center;
  position: relative;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
    cursor: pointer;
  }
`;

const Video = styled.video`
  width: 100%;
  height: auto;
`;

const ArtworkTitle = styled.h3`
  margin: 0;
  font-size: 1.5rem;
  font-family: jost, sans-serif;
  font-weight: 500;
`;

const ArtworkYear = styled.p`
  margin: 0;
  font-size: 1rem;
  font-family: "eb-garamond", serif;
  font-style: italic;
`;

const ArtworkDescription = styled.p`
  margin-left: 25%;
  margin-right: 25%;
  font-size: 1rem;
  font-family: "eb-garamond", serif;
`;


const Gallery = ({ items, oneWork = false }) => {
  const [artworks, setArtworks] = useState([]);
  const [imageIndexes, setImageIndexes] = useState({});
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const videoRef = useRef(null);
  const itemsPerRow = 3;

  useEffect(() => {
    const fetchArtworkData = async () => {
      try {
        const response = await fetch('/artworks.json');
        if (!response.ok) throw new Error('Failed to fetch artwork data');
        const artworkData = await response.json();

        const groupedSources = {
          'WR': ['WR1.PNG', 'WR2.PNG', 'WR3.PNG', 'WR4.PNG']
        };

        const fetchedArtworks = [];
        let i = 0;

        items.forEach((filename) => {
          const groupKey = Object.keys(groupedSources).find(key =>
            groupedSources[key].includes(filename)
          );

          if (groupKey && fetchedArtworks.find(a => a.id === groupKey)) return;

          if (groupKey) {
            const artworkGroup = artworkData.find(art =>
              groupedSources[groupKey].some(src => art.source.includes(src))
            );
            if (artworkGroup) {
              fetchedArtworks.push({
                ...artworkGroup,
                source: groupedSources[groupKey],
                id: groupKey
              });
            }
          } else {
            const artwork = artworkData.find(art => art.source.includes(filename));
            if (artwork) {
              fetchedArtworks.push({
                ...artwork,
                source: filename,
                id: i++
              });
            }
          }
        });

        setArtworks(fetchedArtworks);
        const initialIndexes = {};
        fetchedArtworks.forEach((art, i) => {
          if (Array.isArray(art.source)) initialIndexes[i] = 0;
        });
        setImageIndexes(initialIndexes);
      } catch (error) {
        console.error('Error fetching artwork data:', error);
      }
    };

    fetchArtworkData();
  }, [items]);

  useEffect(() => {
    const interval = setInterval(() => {
      setImageIndexes(prev => {
        const updated = { ...prev };
        for (const key in updated) {
          if (parseInt(key) !== selectedIndex) {
            const currentArtwork = artworks[key];
            const numImages = Array.isArray(currentArtwork?.source) ? currentArtwork.source.length : 1;
            if (numImages > 1) {
              updated[key] = (updated[key] + 1) % numImages;
            }
          }
        }
        return updated;
      });
    }, 3000);
    return () => clearInterval(interval);
  }, [artworks, selectedIndex]);

  const openModal = (index) => {
    setSelectedIndex(index);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedIndex(null);
  };

  const nextImage = () => {
    setImageIndexes(prev => ({
      ...prev,
      [selectedIndex]: (prev[selectedIndex] + 1) % artworks[selectedIndex].source.length
    }));
  };

  const prevImage = () => {
    setImageIndexes(prev => ({
      ...prev,
      [selectedIndex]: prev[selectedIndex] === 0
        ? artworks[selectedIndex].source.length - 1
        : prev[selectedIndex] - 1
    }));
  };

  const itemWidth = 100 / itemsPerRow;

  return (
    <>
      <GalleryContainer>
        {artworks.map((artwork, index) => (
          <ArtworkItem
            key={index}
            width={itemWidth}
            onClick={() => openModal(index)}
          >
            {artwork.type === 'image' ? (
              <img
                src={`/images/${Array.isArray(artwork.source) ? artwork.source[imageIndexes[index] || 0] : artwork.source}`}
                alt={artwork.title}
                style={{ width: '100%' }}
              />
            ) : (
              <Video ref={videoRef} controls autoPlay muted playsInline loop>
                <source src={`/videos/${artwork.source}`} type="video/mp4" />
                Your browser does not support the video tag.
              </Video>
            )}
            {(!oneWork || index % itemsPerRow === 0) && <ArtworkTitle>{artwork.title}</ArtworkTitle>}
            {(!oneWork || index % itemsPerRow === 0) && <ArtworkYear>{artwork.year}</ArtworkYear>}
          </ArtworkItem>
        ))}
      </GalleryContainer>

      {modalOpen && selectedIndex !== null && (
        <ArtworkModal
          artwork={artworks[selectedIndex]}
          selectedImageIndex={imageIndexes[selectedIndex] || 0}
          onClose={closeModal}
          onNextImage={nextImage}
          onPrevImage={prevImage}
        />
      )}
    </>
  );
};

export default Gallery;